import { Trans, useLingui } from "@lingui/react/macro";
import { FilledButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { saveActivityInvoicingCodes } from "@/api/Invoicing";
import { InvoicingPicker } from "@/forms/AddActivityForm/InvoicingPicker";

export const EditInvoicingCodes = ({
  currentProcedureCodes,
  currentProductCodes,
  onSuccess,
  activityId,
  patientId,
}: {
  currentProcedureCodes: string[] | null;
  currentProductCodes: string[] | null;
  onSuccess: () => void;
  activityId: string;
  patientId: string;
}) => {
  const { t } = useLingui();
  const methods = useForm({
    defaultValues: {
      procedureCodes: currentProcedureCodes ?? [],
      productCodes: currentProductCodes ?? [],
    },
  });

  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    reset,
    setError,
  } = methods;

  const queryClient = useQueryClient();

  const { mutate: mutateActivity, isPending: isPendingActivity } = useMutation({
    mutationFn: ({
      activityId,
      patientId,
      procedureCodes,
      productCodes,
    }: {
      activityId: string;
      patientId: string;
      procedureCodes: string[];
      productCodes: string[];
    }) =>
      saveActivityInvoicingCodes({
        activityId,
        patientId,
        procedureCodes: procedureCodes,
        productCodes: productCodes,
      }),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  return (
    <FormProvider {...methods}>
      <Form
        onSubmit={handleSubmit((formData) => {
          const { procedureCodes, productCodes } = formData;
          return mutateActivity({
            activityId,
            patientId: patientId,
            procedureCodes,
            productCodes,
          });
        })}
      >
        {isPendingActivity ? (
          <Loading message={t`Ändrar faktureringskoder`} />
        ) : (
          <>
            {errors.root?.server?.message && !isDirty ? (
              <ErrorMessage message={errors.root.server.message} />
            ) : null}
            <InvoicingPicker patientId={patientId} />
            {isDirty ? (
              <FilledButton type="submit">
                <Trans>Spara ändringar</Trans>
              </FilledButton>
            ) : (
              <></>
            )}
          </>
        )}
      </Form>
    </FormProvider>
  );
};
